import { FaMapMarkerAlt } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api1Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const organizationalBoundaryOptions = [
        'HQ only',
        'All Chilean operations',
        'Specific sites',
        'All operations',
    ];

    const handleBoundaryChange = (option) => {
        const newBoundary = formData.organizationalBoundary?.includes(option)
            ? formData.organizationalBoundary.filter(b => b !== option)
            : [...(formData.organizationalBoundary || []), option];
        updateState({ formData: { ...formData, organizationalBoundary: newBoundary } });
    };

    const setDefaultDates = () => {
        const currentYear = new Date().getFullYear();
        const startDate = `${currentYear}-01-01`;
        const endDate = `${currentYear}-12-31`;
        updateState({ formData: { ...formData, reportingPeriodStart: startDate, reportingPeriodEnd: endDate } });
        toastr.info('Default dates set to current calendar year');
    };

    const loadSampleData = () => {
        const currentYear = new Date().getFullYear();
        const startDate = `${currentYear}-01-01`;
        const endDate = `${currentYear}-12-31`;
        updateState({
            formData: {
                reportingPeriodStart: startDate,
                reportingPeriodEnd: endDate,
                organizationalBoundary: ['All Chilean operations'],
                countriesRegions: ['Chile'],
                marketBasedElectricity: false,
                dataQualityPreference: 'activity',
            }
        });
        toastr.info('Sample data loaded');
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="flex justify-end space-x-3 mb-6">
                        <button
                            onClick={setDefaultDates}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Set Current Year
                        </button>
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    {/* 1.1 Reporting Period */}
                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            1.1 Reporting Period (usually a calendar year)
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            Please select start/end date from the dropdown calendar
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                <input
                                    type="date"
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200"
                                    value={formData.reportingPeriodStart || ''}
                                    onChange={(e) => updateState({ formData: { ...formData, reportingPeriodStart: e.target.value } })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                <input
                                    type="date"
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all duration-200"
                                    value={formData.reportingPeriodEnd || ''}
                                    onChange={(e) => updateState({ formData: { ...formData, reportingPeriodEnd: e.target.value } })}
                                />
                            </div>
                        </div>
                        {formData.reportingPeriodStart && formData.reportingPeriodEnd && (
                            <div className="mt-2 text-sm text-green-600">
                                Selected period: {new Date(formData.reportingPeriodStart).toLocaleDateString()} to {new Date(formData.reportingPeriodEnd).toLocaleDateString()}
                            </div>
                        )}
                    </div>

                    {/* 1.2 Organizational Boundary */}
                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            1.2 Organizational Boundary
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            Which parts of the company do you want to include in the company's carbon footprint? Select all that apply:
                        </p>
                        <div className="space-y-3">
                            {organizationalBoundaryOptions.map((option) => (
                                <label key={option} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200">
                                    <input
                                        type="checkbox"
                                        checked={formData.organizationalBoundary?.includes(option) || false}
                                        onChange={() => handleBoundaryChange(option)}
                                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                                    />
                                    <span className="text-gray-700 font-medium">{option}</span>
                                </label>
                            ))}
                        </div>
                        {formData.organizationalBoundary && formData.organizationalBoundary.length > 0 && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                    <strong>Selected boundaries:</strong> {formData.organizationalBoundary.join(', ')}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* 1.3 Countries/Regions of Operation */}
                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            1.3 Countries/Regions of Operation
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            This will always be 'Chile' for now. In the future, we'll add more countries.
                        </p>
                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <FaMapMarkerAlt className="w-5 h-5 text-gray-500" />
                            <span className="text-gray-700 font-medium">Chile</span>
                        </div>
                    </div>

                    {/* 1.4 Market-based Electricity Choice */}
                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            1.4 Market-based Electricity Choice
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            If a company uses green tariffs/RECs/PPAs then Climatiq will use market-based electricity factors; otherwise location-based.
                        </p>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            <div className="flex items-center space-x-4">
                                <span className={`font-medium ${!formData.marketBasedElectricity ? 'text-green-600' : 'text-gray-500'}`}>
                                    Location-based (default)
                                </span>
                                <button
                                    onClick={() => updateState({ formData: { ...formData, marketBasedElectricity: !formData.marketBasedElectricity } })}
                                    className="relative inline-flex items-center"
                                >
                                    <div className={`w-14 h-7 flex items-center rounded-full p-1 transition-colors duration-200 ${formData.marketBasedElectricity ? 'bg-green-500' : 'bg-gray-300'
                                        }`}>
                                        <div className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-200 ${formData.marketBasedElectricity ? 'translate-x-7' : 'translate-x-0'
                                            }`} />
                                    </div>
                                </button>
                                <span className={`font-medium ${formData.marketBasedElectricity ? 'text-green-600' : 'text-gray-500'}`}>
                                    Market-based
                                </span>
                            </div>
                            <div className="text-sm text-gray-500">
                                {formData.marketBasedElectricity ?
                                    'Using market-based factors (green tariffs/RECs/PPAs)' :
                                    'Using location-based factors (grid average)'}
                            </div>
                        </div>
                    </div>

                    {/* 1.5 Data Quality Preferences */}
                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            1.5 Data Quality Preferences
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            Whether to use activity data (best), or spend-based fallbacks where kWh/liters aren't available.
                        </p>
                        <div className="space-y-3">
                            <label className={`flex items-start space-x-3 cursor-pointer p-4 rounded-lg transition-colors border-2 ${formData.dataQualityPreference === 'activity'
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}>
                                <input
                                    type="radio"
                                    name="dataQuality"
                                    value="activity"
                                    checked={formData.dataQualityPreference === 'activity'}
                                    onChange={(e) => updateState({ formData: { ...formData, dataQualityPreference: e.target.value } })}
                                    className="w-5 h-5 text-green-600 border-gray-300 focus:ring-green-500 mt-1"
                                />
                                <div>
                                    <span className="text-gray-700 font-medium block">Activity data (preferred)</span>
                                    <span className="text-sm text-gray-500">Use kWh, liters, tons when available - Most accurate method</span>
                                </div>
                            </label>
                            <label className={`flex items-start space-x-3 cursor-pointer p-4 rounded-lg transition-colors border-2 ${formData.dataQualityPreference === 'spend-based'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}>
                                <input
                                    type="radio"
                                    name="dataQuality"
                                    value="spend-based"
                                    checked={formData.dataQualityPreference === 'spend-based'}
                                    onChange={(e) => updateState({ formData: { ...formData, dataQualityPreference: e.target.value } })}
                                    className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                                />
                                <div>
                                    <span className="text-gray-700 font-medium block">Spend-based (fallback)</span>
                                    <span className="text-sm text-gray-500">Use when activity data is missing - Less accurate but useful when specific data is unavailable</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Summary Section */}
                    <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuration Summary</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Reporting Period:</span>
                                <span className="font-medium">
                                    {formData.reportingPeriodStart && formData.reportingPeriodEnd
                                        ? `${new Date(formData.reportingPeriodStart).toLocaleDateString()} - ${new Date(formData.reportingPeriodEnd).toLocaleDateString()}`
                                        : 'Not set'
                                    }
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Organizational Boundary:</span>
                                <span className="font-medium">
                                    {formData.organizationalBoundary && formData.organizationalBoundary.length > 0
                                        ? formData.organizationalBoundary.join(', ')
                                        : 'Not set'
                                    }
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Countries/Regions:</span>
                                <span className="font-medium">Chile</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Electricity Method:</span>
                                <span className="font-medium">
                                    {formData.marketBasedElectricity ? 'Market-based' : 'Location-based'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Data Quality Preference:</span>
                                <span className="font-medium">
                                    {formData.dataQualityPreference === 'activity' ? 'Activity Data' : 'Spend-based'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default Api1Form;